/**
 * Unusual CME volume on the chart's own timeframe.
 *
 * rel vol = this bar's volume / a baseline, both per 2m base bar (a 3m/5m
 * candle holds 1-2 / 2-3 whole 2m base candles, same folding as
 * utils/candleSeries.ts, so raw sums would sawtooth).
 *
 * Baselines:
 * - trailing: mean of the previous N eligible bars;
 * - time-of-day: mean of the bars at the same Chicago time of day in the
 *   previous sessions (up to TOD_SESSIONS; needs TOD_MIN_SESSIONS), which
 *   absorbs the RTH open/close surges that a trailing mean flags every day;
 * - auto (default): time-of-day when enough sessions, else trailing.
 * The basis used is reported per bar (tooltip).
 *
 * Eligible = closed (its interval is over: no repaint) and not within
 * REOPEN_WINDOW of a session (re)open. A gap between candles longer than
 * GAP_MS (daily halt, weekend, holiday) is a closure; there are no candles
 * inside it, so the baselines simply walk over it.
 */
import { clampBaseline, RelVolMap, rollingRelVols, type RelVolBaseline, type RelVolBasis } from './relVolBaseline';

export { chicagoMinuteOfDay, relVolBasisText, relVolMisses, TOD_MIN_SESSIONS, TOD_SESSIONS } from './relVolBaseline';
export const REOPEN_WINDOW_MS = 15 * 60_000;
export const BASE_BAR_MS = 120_000;
const GAP_MS = 10 * 60_000;

export type CmeBaseline = RelVolBaseline;

export interface CmeSpikeSettings {
  lookback: number;
  threshold: number;
  baseline: CmeBaseline;
}

export const DEFAULT_CME_SPIKE_SETTINGS: CmeSpikeSettings = { lookback: 30, threshold: 2.0, baseline: 'auto' };

export function clampCmeSpikeSettings(s: Partial<CmeSpikeSettings> | null | undefined): CmeSpikeSettings {
  const n = Number(s?.lookback);
  const t = Number(s?.threshold);
  return {
    lookback: Number.isFinite(n) ? Math.round(Math.min(500, Math.max(5, n))) : DEFAULT_CME_SPIKE_SETTINGS.lookback,
    threshold: Number.isFinite(t) ? Math.min(20, Math.max(1.1, t)) : DEFAULT_CME_SPIKE_SETTINGS.threshold,
    baseline: clampBaseline(s?.baseline),
  };
}

export interface RelVol {
  /** Chart bar open, seconds. */
  time: number;
  relVol: number;
  v: number;
  /** Baseline volume for a bar of this size. */
  avg: number;
  basis: RelVolBasis;
  /** Bars (trailing) or sessions (time-of-day) behind the baseline. */
  samples: number;
}

/** Whole 2m base bars whose open falls in [t, t + interval). */
export function baseSlots(timeSec: number, intervalMs: number): number {
  if (intervalMs <= BASE_BAR_MS) return 1;
  const start = timeSec * 1000;
  const first = Math.ceil(start / BASE_BAR_MS) * BASE_BAR_MS;
  return Math.max(1, Math.floor((start + intervalMs - 1 - first) / BASE_BAR_MS) + 1);
}

export interface VolumeBar {
  time: number;
  volume: number;
}

export interface RelVolOptions {
  /** Only bars at/after this time (seconds) are returned: the chart's first bar. Earlier bars are baseline only. */
  fromSec?: number;
}

/**
 * The chart's bars preceded by baseline history (#593). The chart holds ~1
 * day of 2m candles, time of day needs ~10 sessions, so the CME 2m volumes
 * fetched apart from the chart (cmeBaselineHistoryStore) are folded to the
 * chart timeframe (floor(t/I)*I, like the candles) and put in front of the
 * chart's first bar. Never shown: only the chart's bars get rel vols
 * (`fromSec`). The chart's first bar may be a partial bucket (history starts
 * mid-interval); the history holds the whole bucket, so the larger volume wins.
 */
export function withBaselineHistory(chart: readonly VolumeBar[], history2m: readonly VolumeBar[] | null | undefined, timeframeMinutes: number): VolumeBar[] {
  if (!history2m || history2m.length === 0 || chart.length === 0) return chart.slice();
  const first = chart[0].time;
  const intervalMs = Math.max(60_000, Math.round(timeframeMinutes * 60_000));
  const folded = new Map<number, number>();
  for (const b of history2m) {
    const t = intervalMs <= BASE_BAR_MS ? b.time : Math.floor((b.time * 1000) / intervalMs) * (intervalMs / 1000);
    if (t > first) continue;
    folded.set(t, (folded.get(t) ?? 0) + (b.volume || 0));
  }
  const out: VolumeBar[] = [...folded].filter(([t]) => t < first).sort((a, b) => a[0] - b[0]).map(([time, volume]) => ({ time, volume }));
  const head = folded.get(first);
  out.push(head !== undefined && head > (chart[0].volume || 0) ? { time: first, volume: head } : chart[0]);
  for (let i = 1; i < chart.length; i++) out.push(chart[i]);
  return out;
}

/**
 * Rel vol of every eligible closed bar (keyed by time, seconds); `misses` =
 * bars forced time of day could not rate (too few sessions), see relVolMisses.
 */
export function cmeRelVols(
  bars: readonly VolumeBar[],
  timeframeMinutes: number,
  settings: Partial<CmeSpikeSettings>,
  nowMs: number,
  opts: RelVolOptions = {},
): Map<number, RelVol> {
  const { lookback, baseline } = clampCmeSpikeSettings(settings);
  const intervalMs = Math.max(60_000, Math.round(timeframeMinutes * 60_000));
  const out = new RelVolMap<RelVol>();
  const from = opts.fromSec ?? -Infinity;
  // Eligible bars (closed, outside a reopen window) in order; the shared
  // baseline walks them (utils/relVolBaseline.ts, same as the Vest 🐋).
  const eligible: { time: number; perSlot: number; slots: number; v: number }[] = [];
  let prev: number | null = null;
  let reopenAt: number | null = null;
  for (const b of bars) {
    const t = b.time * 1000;
    if (prev === null || t - prev > Math.max(intervalMs, GAP_MS)) reopenAt = t;
    prev = t;
    if (t + intervalMs > nowMs) continue; // forming: never evaluated
    if (reopenAt !== null && t - reopenAt < REOPEN_WINDOW_MS) continue;
    const slots = baseSlots(b.time, intervalMs);
    eligible.push({ time: b.time, perSlot: (b.volume || 0) / slots, slots, v: b.volume });
  }
  rollingRelVols(
    eligible,
    lookback,
    baseline,
    (p, hit) => {
      if (p.time >= from) out.set(p.time, { time: p.time, v: p.v, ...hit });
    },
    (p, miss) => {
      if (p.time >= from) out.misses.set(p.time, miss);
    },
  );
  return out;
}

/**
 * Rel vol of the chart's CLOSED bars, judged by the data rather than the
 * browser clock: `bars` are the completed chart bars (ChartView's series
 * snapshot, every base candle except the forming one) and `formingMs` is the
 * forming base candle's open. Every chart bar that starts before the chart bar
 * holding the forming candle is closed; that one (on 3m/5m+ it may already
 * hold closed base candles) is not.
 *
 * The snapshot is rebuilt when the next base candle opens, ~1s after the
 * boundary. Judging "closed" with Date.now() there dropped the just-closed bar
 * whenever the browser clock ran even a second behind the feed, and nothing
 * looked again until the next bar: the marker showed only after a reload.
 * Without a forming candle (nothing loaded yet) the clock is the fallback.
 */
export function closedCmeRelVols(
  bars: readonly VolumeBar[],
  formingMs: number | null | undefined,
  timeframeMinutes: number,
  settings: Partial<CmeSpikeSettings>,
  nowMs: number = Date.now(),
  opts: RelVolOptions = {},
): Map<number, RelVol> {
  const intervalMs = Math.max(60_000, Math.round(timeframeMinutes * 60_000));
  const cutoff = typeof formingMs === 'number' && Number.isFinite(formingMs) ? Math.floor(formingMs / intervalMs) * intervalMs : nowMs;
  return cmeRelVols(bars, timeframeMinutes, settings, cutoff, opts);
}

/** "2.4x" label for a marker. */
export function relVolLabel(relVol: number): string {
  return `${relVol >= 10 ? Math.round(relVol) : relVol.toFixed(1)}x`;
}

/**
 * CME volume bar colour: green/red at 50% like always; a bar flagged as
 * unusual CME volume (volume-pane spikes) is drawn at full strength. Not tied
 * to the "2.4x" label setting: hiding the multiples keeps the highlight.
 */
export function cmeVolumeColor(c: { time: unknown; open: number; close: number }, spikes: ReadonlySet<number>): string {
  const up = c.close >= c.open;
  const a = typeof c.time === 'number' && spikes.has(c.time) ? 1 : 0.5;
  return up ? `rgba(38, 166, 154, ${a})` : `rgba(239, 83, 80, ${a})`;
}
